import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import type { IndicatorTerm } from "@/types/indicators.types";

export interface LineOption {
  id: string;
  name: string;
}
export interface ProductOption {
  id: string;
  name: string;
}

interface TermRowProps {
  term: IndicatorTerm;
  lines: LineOption[];
  /** Só os produtos que a linha escolhida registra naquele sentido (FR-028). */
  productsFor: (lineId: string, role: IndicatorTerm["role"]) => ProductOption[];
  onChange: (patch: Partial<IndicatorTerm>) => void;
  onRemove: () => void;
}

const ROLE_LABEL: Record<IndicatorTerm["role"], string> = {
  PRODUCES: "produz",
  CONSUMES: "consome",
  SCRAPS: "refuga",
};

/**
 * Spec 082 — uma FRASE: "a linha X produz | consome | refuga o produto Y". É o mesmo vocabulário
 * do contador de máquina, e é assim que o gestor monta a fórmula sem escrever fórmula nenhuma.
 */
export function TermRow({ term, lines, productsFor, onChange, onRemove }: TermRowProps) {
  const produtos = productsFor(term.lineId, term.role);
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[13px]">
      <span className="text-text-secondary">A linha</span>
      <Select
        aria-label="Linha de produção da frase"
        value={term.lineId}
        onChange={(e) => onChange({ lineId: e.target.value, productId: "" })}
      >
        <option value="">escolha</option>
        {lines.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
      </Select>
      <Select
        aria-label="Sentido da frase"
        value={term.role}
        onChange={(e) => onChange({ role: e.target.value as IndicatorTerm["role"], productId: "" })}
      >
        {(Object.keys(ROLE_LABEL) as IndicatorTerm["role"][]).map((r) => (
          <option key={r} value={r}>
            {ROLE_LABEL[r]}
          </option>
        ))}
      </Select>
      <Select
        aria-label="Produto da frase"
        value={term.productId}
        onChange={(e) => onChange({ productId: e.target.value })}
        disabled={!term.lineId}
      >
        <option value="">
          {/* Produto que a linha nunca registrou não aparece na lista (FR-028): oferecê-lo seria
              prometer um número que não existe. */}
          {produtos.length === 0 ? "nenhum produto registrado" : "escolha o produto"}
        </option>
        {produtos.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </Select>
      <Button variant="outline" size="sm" aria-label="Remover frase" onClick={onRemove}>
        <Trash2 size={14} />
      </Button>
    </div>
  );
}
